import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { productionApi, productsApi } from '../../../services/api';
import { UNIT_OPTIONS } from '../../../constants';
import { SearchableSelect, Option } from '../../../components/SearchableSelect';

interface AddProductionFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const AddProductionForm: React.FC<AddProductionFormProps> = ({ onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    productId: '',
    quantity: 0,
    unit: 'cajas' as string,
    producedDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD
    producedTime: new Date().toTimeString().slice(0, 5),  // HH:MM
    observations: '',
  });

  // Search handler for products
  const handleProductSearch = async (query: string): Promise<Option[]> => {
    try {
      const response = await productsApi.getAll({
        search: query,
        isActive: true,
        pageSize: 50
      });
      return response.items.map(p => ({
        value: p.id,
        label: p.name,
        subLabel: p.externalId || undefined
      }));
    } catch (err) {
      console.error('Error searching products:', err);
      return [];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.productId) {
      setError('Por favor selecciona un producto');
      return;
    }

    if (formData.quantity <= 0) {
      setError('La cantidad debe ser mayor a 0');
      return;
    }

    try {
      setIsSubmitting(true);

      // Combine date and time into ISO string
      const producedAt = new Date(`${formData.producedDate}T${formData.producedTime}`).toISOString();

      await productionApi.createRecord({
        productId: formData.productId,
        quantity: formData.quantity,
        unit: formData.unit,
        producedAt: producedAt,
        observations: formData.observations.trim() || null,
      });

      onSuccess();
    } catch (err: any) {
      console.error('Error creating production record:', err);
      setError(err.message || 'Error al registrar la producción');
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Product Select */}
        <div className="md:col-span-2">
          <SearchableSelect
            label="Producto"
            placeholder="Buscar producto..."
            value={formData.productId}
            onChange={(value) => setFormData({ ...formData, productId: value })}
            onSearch={handleProductSearch}
            minSearchLength={2}
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Quantity */}
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-slate-700 mb-1">
            Cantidad <span className="text-red-500">*</span>
          </label>
          <input
            id="quantity"
            type="number"
            min="0.01"
            step="0.01"
            value={formData.quantity}
            onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="0"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Unit */}
        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-slate-700 mb-1">
            Unidad <span className="text-red-500">*</span>
          </label>
          <select
            id="unit"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            disabled={isSubmitting}
            required
          >
            {UNIT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Production Date */}
        <div>
          <label htmlFor="producedDate" className="block text-sm font-medium text-slate-700 mb-1">
            Fecha de Producción <span className="text-red-500">*</span>
          </label>
          <input
            id="producedDate"
            type="date"
            value={formData.producedDate}
            onChange={(e) => setFormData({ ...formData, producedDate: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Production Time */}
        <div>
          <label htmlFor="producedTime" className="block text-sm font-medium text-slate-700 mb-1">
            Hora de Producción <span className="text-red-500">*</span>
          </label>
          <input
            id="producedTime"
            type="time"
            value={formData.producedTime}
            onChange={(e) => setFormData({ ...formData, producedTime: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            disabled={isSubmitting}
            required
          />
        </div>

        {/* Observations */}
        <div className="md:col-span-2">
          <label htmlFor="observations" className="block text-sm font-medium text-slate-700 mb-1">
            Observaciones
          </label>
          <textarea
            id="observations"
            value={formData.observations}
            onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Ej. Lote matutino, calibre especial..."
            rows={2}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors font-medium disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
        >
          {isSubmitting && (
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          )}
          <Save size={18} />
          Registrar Producción
        </button>
      </div>
    </form>
  );
};
